import { WbTextField } from '@agilelab/plugin-wb-platform';
import { generateIdentifier } from '@agilelab/plugin-wb-platform-common';
import { useCallback, useEffect, useMemo } from 'react';
import { isHidden } from '../../utils';
import { IdentifierPickerProps } from './schema';
import { extractFieldPathFromRjsfId } from '../../MultiStepJsonForm/utils/extractFieldPathFromRjsfId';

export const IdentifierPicker = (props: IdentifierPickerProps) => {
  const {
    onChange,
    schema: {
      title = 'Identifier',
      description = 'Unique identifier of the component',
    },
    required,
    rawErrors,
    formData,
    uiSchema,
    idSchema,
    placeholder,
    formContext,
  } = props;
  const autoFocus = uiSchema['ui:autofocus'];
  const domain = uiSchema['ui:options']?.domainField ?? 'domain';
  const name = uiSchema['ui:options']?.nameField ?? 'name';

  // Check if dependency manager is available
  const dependencyManager = formContext?.dependencyManager;

  // Track whether dependency manager was ever available (new architecture)
  const hasUsedDependencyManager = useMemo(() => ({ current: false }), []);
  if (dependencyManager) {
    hasUsedDependencyManager.current = true;
  }

  // Extract canonical field path from RJSF's idSchema
  const fieldPath = useMemo(() => {
    const id = idSchema?.$id || '';
    return extractFieldPathFromRjsfId(id);
  }, [idSchema]);

  // Derive specific dependency values to avoid depending on unstable formContext reference
  const nameValue = formContext[name];
  const domainValue = formContext[domain];

  // OLD ARCHITECTURE: Auto-compute identifier when dependencies change
  // This will be skipped when dependency manager has ever been enabled
  useEffect(() => {
    if (hasUsedDependencyManager.current) {
      return;
    }

    const id = generateIdentifier(nameValue, domainValue) ?? '';
    if (formData !== id) onChange(id);
  }, [nameValue, domainValue, formData, onChange, hasUsedDependencyManager]);

  // NEW ARCHITECTURE: Register dependency handler
  // Re-register when middleware ID changes (middleware is recreated on step switch)
  const middlewareId = dependencyManager?.id;
  useEffect(() => {
    if (!dependencyManager?.isReady || !fieldPath) {
      return;
    }

    // Register dependencies (so the middleware knows identifier depends on domain and name)
    if (dependencyManager?.registerDependencies) {
      dependencyManager.registerDependencies(fieldPath, [domain, name]);
    }

    // Register handler for this field
    dependencyManager.registerHandler(
      fieldPath,
      (
        _fieldPath: string,
        triggerPath: string,
        triggerValue: any,
        currentFormData: any,
      ) => {
        // Use the trigger value if it's one of our dependencies, otherwise use form data
        const handlerNameValue =
          triggerPath === name ? triggerValue : currentFormData[name];
        const handlerDomainValue =
          triggerPath === domain ? triggerValue : currentFormData[domain];

        return generateIdentifier(handlerNameValue, handlerDomainValue) ?? '';
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyManager?.isReady, middlewareId, fieldPath, domain, name]);

  // Handle manual changes
  const handleChange = useCallback(
    (value: string) => {
      // Notify dependency manager or fallback to onChange
      if (dependencyManager?.notifyUpdate && fieldPath) {
        dependencyManager.notifyUpdate({
          fieldPath,
          value,
          source: 'user',
          intent: 'manual',
        });
      } else {
        onChange(value);
      }
    },
    [onChange, dependencyManager, fieldPath],
  );

  return (
    <WbTextField
      id={idSchema?.$id}
      style={{ display: isHidden(uiSchema) ? 'none' : undefined }}
      label={title}
      placeholder={placeholder}
      helperText={description}
      disabled={!(uiSchema['ui:options']?.allowArbitraryValues ?? true)}
      required={required}
      value={formData ?? ''}
      onChange={({ target: { value } }) => handleChange(value)}
      margin="normal"
      error={rawErrors?.length > 0 && !formData}
      inputProps={{ autoFocus }}
    />
  );
};
